import debounce from 'lodash/debounce'
import type { Ref } from 'vue'

export interface UseKftLocalStorageOptions<T> {
  /** Which Web Storage API to use. Default: `local` (persists across browser sessions). */
  storage?: 'local' | 'session'
  /** Controls what is written to storage; defaults to the full state value. */
  getPersistableSnapshot?: (value: T) => unknown
}

/** Debounce window for persistence writes; coalesces bursts of mutations into one serialize. */
const WRITE_DEBOUNCE_MS = 250

const getWebStorage = (kind: 'local' | 'session'): Storage | undefined => {
  if (!import.meta.client || typeof window === 'undefined') {
    return undefined
  }
  try {
    return kind === 'session' ? window?.sessionStorage : window?.localStorage
  } catch (error) {
    console.log('Error accessing Web Storage', error)
    return undefined
  }
}

interface KeyPersistence {
  /** Read storage back into state once (idempotent via the shared `loaded` flag). */
  load: () => void
  /** Re-read storage into state if another page changed it (bfcache restore). */
  reload: () => void
  /** Force any pending debounced write to happen now. */
  flush: () => void
  /** Persist any pending write, cancel the debounce timer and stop the watcher. */
  dispose: () => void
  /** How many mounted consumers currently share this controller. */
  consumers: number
}

// `data`/`loaded` are shared `useState` per key, so the deep watcher and the
// debounce are created ONCE per storage key — not once per component that reads
// the key (that multiplied watchers and redundant serialize work). The
// controller is reference-counted: it lives while at least one consumer is
// mounted and is disposed (and removed from this map) when the last one
// unmounts, so dynamic keys (per-event/per-subdomain cart keys) don't leak
// watchers/entries for the life of the tab.
const persistenceByKey = new Map<string, KeyPersistence>()

/** Flush every key's pending debounced write to storage now. */
const flushAllPending = () => {
  for (const persistence of persistenceByKey.values()) persistence.flush()
}

// The raw string last read from / written to storage per key, so a reload can
// skip keys no other page touched (re-assigning would fire every deep watcher
// and drop fields that getPersistableSnapshot leaves out of storage).
const lastSerializedByKey = new Map<string, string | null>()
// Every key's shared `loaded` flag. It outlives the key's controller (which is
// disposed when the last consumer unmounts), so it is tracked separately.
const loadedFlagByKey = new Map<
  string,
  { loaded: Ref<boolean>; storageKind: 'local' | 'session' }
>()

const readRaw = (key: string, storageKind: 'local' | 'session') => {
  try {
    return getWebStorage(storageKind)?.getItem(key) ?? null
  } catch (error) {
    console.error('Error accessing Web Storage', error)
    return null
  }
}

/**
 * Bring every key in line with storage: re-read mounted keys that another page
 * changed, and mark changed unmounted keys as not loaded so their next consumer
 * reads storage again instead of trusting the stale shared state.
 */
const reloadAll = () => {
  for (const [key, { loaded, storageKind }] of loadedFlagByKey) {
    const persistence = persistenceByKey.get(key)
    if (persistence) {
      persistence.reload()
    } else if (readRaw(key, storageKind) !== lastSerializedByKey.get(key)) {
      loaded.value = false
    }
  }
}

// Full-page navigations can't wait on the debounce timer; pagehide/
// visibilitychange flush pending writes first. Registered ONCE for all keys —
// not per key — so dynamic keys (per-event/per-subdomain cart keys) don't
// accumulate window/document listeners for the life of the tab.
let flushListenersRegistered = false
const registerFlushListeners = () => {
  if (flushListenersRegistered || !import.meta.client) return
  flushListenersRegistered = true
  window.addEventListener('pagehide', flushAllPending)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAllPending()
  })
}

const createKeyPersistence = <T>(
  key: string,
  data: Ref<T>,
  loaded: Ref<boolean>,
  storageKind: 'local' | 'session',
  options?: UseKftLocalStorageOptions<T>
): KeyPersistence => {
  // Debounced persistence: a single user action re-assigns/mutates `data`
  // several times on the same tick; debouncing coalesces those into one
  // JSON.stringify instead of serializing synchronously per mutation (INP cost).
  const write = () => {
    const store = getWebStorage(storageKind)
    if (!store) return
    try {
      const snapshot = options?.getPersistableSnapshot
        ? options.getPersistableSnapshot(data.value)
        : data.value
      const serialized = JSON.stringify(snapshot)
      store.setItem(key, serialized)
      lastSerializedByKey.set(key, serialized)
    } catch (error) {
      console.error('Error accessing Web Storage', error)
    }
  }

  const debouncedWrite = debounce(write, WRITE_DEBOUNCE_MS)
  const flush = () => debouncedWrite.flush()

  // Set while reading storage back into `data` so the load-assignment does not
  // trip the watch and re-schedule a write of the value we just read.
  let isLoading = false
  const readFromStorage = (storedData: string | null) => {
    lastSerializedByKey.set(key, storedData)
    if (storedData) {
      // try/finally so a malformed/legacy value can't leave isLoading stuck
      // true — that would make the watch suppress every subsequent write for
      // the whole page session (silent data loss).
      isLoading = true
      try {
        data.value = JSON.parse(storedData)
      } catch (error) {
        console.error('Error parsing stored Web Storage value', error)
      } finally {
        isLoading = false
      }
    }
    loaded.value = true
  }

  const load = () => {
    if (loaded.value) return
    readFromStorage(readRaw(key, storageKind))
  }

  const reload = () => {
    const storedData = readRaw(key, storageKind)
    if (storedData === lastSerializedByKey.get(key)) return
    readFromStorage(storedData)
  }

  // Detached scope so the single shared watcher isn't tied to whichever
  // component happened to mount this key first — it is stopped explicitly in
  // dispose() when the last consumer unmounts.
  // flush: 'sync' so scheduling happens on the mutation tick — a same-tick
  // page-hide then finds a pending write and flushes it; the expensive
  // JSON.stringify stays debounced/coalesced in `write`.
  const scope = effectScope(true)
  scope.run(() => {
    watch(
      data,
      () => {
        if (!isLoading) debouncedWrite()
      },
      { deep: true, flush: 'sync' }
    )
  })

  const dispose = () => {
    debouncedWrite.flush() // persist a pending write before we stop watching
    debouncedWrite.cancel()
    scope.stop()
  }

  return { load, reload, flush, dispose, consumers: 0 }
}

// The first consumer of a key builds the controller; later consumers reuse it.
// `create` is a thunk (not the ingredients) to make it explicit at the call
// site that its captured data/options apply only on a cache miss — a second
// consumer passing different options does NOT reconfigure the live controller.
const getKeyPersistence = (
  key: string,
  create: () => KeyPersistence
): KeyPersistence => {
  registerFlushListeners()
  let persistence = persistenceByKey.get(key)
  if (!persistence) {
    persistence = create()
    persistenceByKey.set(key, persistence)
  }
  return persistence
}

export const useKftLocalStorage = <T>(
  key: string,
  defaultValue: T,
  options?: UseKftLocalStorageOptions<T>
) => {
  const storageKind = options?.storage ?? 'local'

  const data = useState(key, () => defaultValue)
  /** Shared so every caller of the same storage key sees the same load flag (avoids first-paint gaps). */
  const loaded = useState<boolean>(`${key}:kft-webstorage-loaded`, () => false)
  if (import.meta.client) loadedFlagByKey.set(key, { loaded, storageKind })

  // The persistence machinery (watch + debounce) is created once per storage
  // key and shared across every consumer of that key. It is reference-counted:
  // the last consumer to unmount disposes it and drops the map entry.
  const persistence = import.meta.client
    ? getKeyPersistence(key, () =>
        createKeyPersistence(key, data, loaded, storageKind, options)
      )
    : undefined

  if (persistence) {
    persistence.consumers += 1
    onScopeDispose(() => {
      persistence.consumers -= 1
      if (persistence.consumers <= 0) {
        persistence.dispose()
        // Only drop the entry if it is still this controller (never clobber a
        // replacement created for the same key after we disposed).
        if (persistenceByKey.get(key) === persistence) {
          persistenceByKey.delete(key)
        }
      }
    })
  }

  const loadFromStorage = () => persistence?.load()
  const flush = () => persistence?.flush()

  onMounted(() => {
    loadFromStorage()
  })

  return { data, loaded, loadFromStorage, flush }
}

/**
 * Force every pending debounced storage write to disk synchronously. Call this
 * right before a hard navigation (`window.location.href = …`) that mutates
 * persisted state first (e.g. clearing the cart on checkout completion): the
 * redirect would otherwise outrun the 250ms debounce, and the pagehide flush is
 * not guaranteed to land before teardown on every browser (iOS Safari).
 */
export const flushKftLocalStorageWrites = () => flushAllPending()

/**
 * Re-read every persisted key from storage. Call this when the page is
 * restored from the back/forward cache: the restored page keeps the in-memory
 * state it had when it was left, while the page navigated to in between (e.g.
 * checkout clearing the cart) may have changed storage since.
 */
export const reloadKftLocalStorageFromStorage = () => reloadAll()
